import { ToucanColors, ToucanComponents } from '@jointoucan/toucan-design';
import { ComponentRenderData, initPlasmicLoader } from '@plasmicapp/loader-nextjs';

import { AlertTemplate, AlertTypes } from '~/components/plasmic/Alert';
import { SendEventOnClick } from '~/components/plasmic/attachments/SendEventOnClick';
import { SendEventOnView } from '~/components/plasmic/attachments/SendEventOnView';
import Layout from '~/components/shared/Layout';
import { NavbarElement } from '~/components/shared/Navbar/types';
import { SignUpModalButton } from '~/components/shared/SignUpModalButton';
import { env, Environment } from '~/lib/config';

/**
 * Plasmic project IDs and API tokens are public.
 */
export const PLASMIC = initPlasmicLoader({
  projects: [
    {
      id: 'b36BkM5BJYpQMRX8Tb4Nhv',
      token: 'tGhmTY2YERM5hNuzmCDaNSemoTytbqkO4i7RO3kxDG8aQr2YsfHKbl7JhVOrM7ASIgOL1z5pwzP6G3dGr5GDA',
    },
  ],
  /**
   * In production, show only published revsions.
   * Elsewhere, show all revisions.
   */
  preview: env !== Environment.Production,
});

export const revalidateInterval = env === Environment.Production ? 600 : 10;

const manuallyLoadedPaths = ['/', '/about', '/home'];

/**
 * Fetch the list of pages created in Plasmic. For a couple reasons, we should
 * exclude any pages that use Plasmic but are hard-coded into the Next.js repo,
 * such as pages/about.tsx.
 * 1. If getStaticPaths in pages/[...catchall]/index.tsx returns one of these
 * pages, Next will try to generate the page twice, and an error will occur.
 * See https://github.com/jointoucan/toucan-website/pull/2023 for more context.
 * 2. We don't want to include these pages in the sitemap multiple times.
 */
export const fetchAutoLoadedPages = async () => {
  const pages = await PLASMIC.fetchPages();
  return pages.filter((page) => !manuallyLoadedPaths.includes(page.path));
};

export const getSeoOverrides = (plasmicData: ComponentRenderData) => {
  const { title, description, openGraphImageUrl } = plasmicData.entryCompMetas[0].pageMetadata ?? {};
  const titleAndDescription = {
    ...(title ? { title } : {}),
    ...(description ? { description } : {}),
  };
  return {
    ...titleAndDescription,
    openGraph: {
      ...titleAndDescription,
      images: openGraphImageUrl ? [{ url: openGraphImageUrl }] : [],
    },
  };
};

export interface PlasmicProps {
  plasmicData?: ComponentRenderData;
  plasmicPath?: string;
  queryCache?: Record<string, any>;
}

PLASMIC.registerComponent(Layout, {
  name: 'Layout',
  styleSections: false, // This component does not accept className,
  props: {
    children: {
      type: 'slot',
      defaultValue: [
        {
          type: 'text',
          value: 'Insert page content here',
        },
      ],
    },
    isNavbarRightContentVisible: {
      type: 'boolean',
      defaultValue: true,
      displayName: 'Is navbar right content visible?',
    },
    isContained: {
      type: 'boolean',
      defaultValue: true,
      displayName: 'Is contained?',
    },
    isNavbarVisible: {
      type: 'boolean',
      defaultValue: true,
      displayName: 'Is navbar visible?',
    },
    hasFooter: {
      type: 'boolean',
      defaultValue: true,
      displayName: 'Has footer?',
    },
    isDisabledToucanLogo: {
      type: 'boolean',
      displayName: 'Is Toucan logo disabled?',
    },
    navbarVisibleElements: {
      type: 'choice',
      displayName: 'Navbar visible elements',
      multiSelect: true,
      options: Object.values(NavbarElement),
      defaultValue: [NavbarElement.HomeLink, NavbarElement.DashboardLink],
    },
    customNavbar: {
      type: 'slot',
      hidePlaceholder: true,
    },
  },
});

PLASMIC.registerComponent(SendEventOnClick, {
  name: 'SendEventOnClick',
  isAttachment: true,
  styleSections: false, // This component does not accept className,
  props: {
    children: 'slot',
    elementName: {
      displayName: 'Element name',
      type: 'string',
    },
    eventProperties: {
      type: 'code',
      displayName: 'Event properties',
      lang: 'json',
      defaultValue: '{}',
    },
  },
});

PLASMIC.registerComponent(SendEventOnView, {
  name: 'SendEventOnView',
  isAttachment: true,
  styleSections: false, // This component does not accept className,
  props: {
    children: 'slot',
    elementName: {
      displayName: 'Element name',
      type: 'string',
    },
    eventProperties: {
      type: 'code',
      displayName: 'Event properties',
      lang: 'json',
      defaultValue: '{}',
    },
  },
});

PLASMIC.registerComponent(ToucanComponents.Button, {
  name: 'Button',
  props: {
    type: {
      type: 'choice',
      options: ['primary', 'secondary', 'utility', 'urgent', 'alert', 'text'],
      defaultValue: 'primary',
    },
    children: {
      type: 'slot',
      defaultValue: [
        {
          type: 'text',
          value: 'Insert button content here',
        },
      ],
    },
    size: {
      type: 'choice',
      options: ['small', 'medium', 'large'],
      defaultValue: 'medium',
    },
    isDisabled: 'boolean',
    isReversed: 'boolean',
    href: 'string',
  },
});

PLASMIC.registerComponent(AlertTemplate, {
  name: 'Alert',
  props: {
    children: {
      type: 'slot',
      defaultValue: [
        {
          type: 'text',
          value: 'Insert alert content here',
        },
      ],
    },
    title: {
      type: 'slot',
      defaultValue: [
        {
          type: 'text',
          value: 'Insert alert title here',
        },
      ],
    },
    severity: {
      type: 'choice',
      options: Object.values(AlertTypes),
      defaultValue: AlertTypes.Success,
    },
  },
});

PLASMIC.registerComponent(ToucanComponents.Card, {
  name: 'Card',
  props: {
    children: {
      type: 'slot',
      defaultValue: [
        {
          type: 'text',
          value: 'Insert card content here',
        },
      ],
    },
    hasPadding: {
      type: 'boolean',
      defaultValue: true,
    },
    isDarkMode: 'boolean',
    maxWidth: {
      type: 'string',
      defaultValue: '448px',
    },
  },
});

PLASMIC.registerComponent(ToucanComponents.ToucanIcon, {
  name: 'ToucanIcon',
  props: {
    icon: {
      displayName: 'iconName',
      type: 'string',
      defaultValue: 'check',
    },
    color: {
      type: 'string',
      defaultValue: ToucanColors.gray[300],
    },
    width: {
      type: 'number',
      defaultValue: 16,
    },
    height: {
      type: 'number',
      defaultValue: 16,
    },
  },
});

PLASMIC.registerComponent(SignUpModalButton, {
  name: 'GetStartedButton',
  props: {
    buttonLocation: {
      type: 'string',
      defaultValue: 'Plasmic page',
      displayName: 'Button location (for analytics)',
    },
    buttonSize: {
      type: 'choice',
      options: ['small', 'medium', 'large'],
      defaultValue: 'medium',
      displayName: 'size',
    },
    trackingEventName: {
      type: 'string',
      defaultValue: 'Sign-Up CTA Modal Clicked',
      displayName: 'Name of click event (required)',
    },
  },
});
